/**
 * Application shell (§7.1).
 *
 * Top bar: sim clock, speed, weather, price ticker, revenue to date. Left rail: the eight views.
 * Everything else is the active view. Keyboard shortcuts live here so they work from any view.
 */

import { useEffect } from 'react';
import { useSession, type ViewId } from '../store/session';
import { useEngine } from './useEngine';
import { getSite } from '../sites';
import { getScenario } from '../scenarios';
import { SPEEDS, type Speed } from '../worker/protocol';
import { money, price as fmtPrice, temperature } from './format';
import { ConfirmDialog } from './widgets/Confirm';
import { GlossaryDrawer } from './glossary/GlossaryDrawer';
import { Launcher } from './views/Launcher';
import { ControlRoom } from './views/ControlRoom';
import { ReservoirForecast } from './views/ReservoirForecast';
import { PowerhouseView } from './views/PowerhouseView';
import { SpillwayView } from './views/SpillwayView';
import { RiverView } from './views/RiverView';
import { MarketView } from './views/MarketView';
import { AlarmsComms } from './views/AlarmsComms';
import { Debrief } from './views/Debrief';

const VIEWS: { id: ViewId; label: string; key: string }[] = [
  { id: 'control-room', label: 'Control Room', key: 'U' },
  { id: 'reservoir', label: 'Reservoir & Forecast', key: 'G' },
  { id: 'powerhouse', label: 'Powerhouse', key: 'L' },
  { id: 'spillway', label: 'Spillway', key: 'R' },
  { id: 'river', label: 'River', key: 'M' },
  { id: 'market', label: 'Market & Schedule', key: 'A' },
  { id: 'alarms', label: 'Alarms & Comms', key: 'K' },
];

export function App(): JSX.Element {
  const phase = useSession((s) => s.phase);
  const view = useSession((s) => s.view);
  const setView = useSession((s) => s.setView);
  const snap = useSession((s) => s.snapshot);
  const paused = useSession((s) => s.paused);
  const speed = useSession((s) => s.speed);
  const fault = useSession((s) => s.fault);
  const toast = useSession((s) => s.toast);
  const setToast = useSession((s) => s.setToast);
  const settings = useSession((s) => s.settings);
  const updateSettings = useSession((s) => s.updateSettings);
  const scenarioId = useSession((s) => s.scenarioId);
  const siteId = useSession((s) => s.siteId);
  const secondarySnapshot = useSession((s) => s.secondarySnapshot);
  const activeSite = useSession((s) => s.activeSite);
  const setActiveSite = useSession((s) => s.setActiveSite);
  const primarySnapshot = useSession((s) => s.primarySnapshot);
  const engine = useEngine();

  // Keyboard: space pause, 1–6 speed, view hotkeys (§7.2).
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      const target = e.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
      if (useSession.getState().pendingConfirm) return;
      if (e.key === ' ') {
        e.preventDefault();
        engine.togglePause();
        return;
      }
      const n = Number(e.key);
      if (n >= 1 && n <= SPEEDS.length) {
        e.preventDefault();
        engine.setSpeed(SPEEDS[n - 1] as Speed);
        return;
      }
      const hit = VIEWS.find((v) => v.key.toLowerCase() === e.key.toLowerCase());
      if (hit) {
        e.preventDefault();
        setView(hit.id);
      }
      if (e.key === '?') updateSettings({ showGlossary: !settings.showGlossary });
      // Tab between the two consoles in a cascade run.
      if (e.key.toLowerCase() === 'c' && useSession.getState().secondarySnapshot) {
        e.preventDefault();
        setActiveSite(useSession.getState().activeSite === 'primary' ? 'secondary' : 'primary');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [engine, setView, settings.showGlossary, updateSettings, setActiveSite]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = window.setTimeout(() => setToast(null), 2600);
    return () => window.clearTimeout(t);
  }, [toast, setToast]);

  if (phase === 'launcher') return <Launcher />;

  const site = getSite(siteId);
  const scenario = getScenario(scenarioId);
  const unacked = snap ? snap.alarms.filter((a) => a.status === 'unacked-active').length : 0;
  const worst = snap?.alarms[0]?.priority ?? null;

  return (
    <div className="app">
      {/* ── Top bar ──────────────────────────────────────────────────────────── */}
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--s4)',
          padding: '6px var(--s4)',
          borderBottom: '1px solid var(--line)',
          background: 'var(--surface-1)',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ minWidth: 150 }}>
          <div className="label">{site.name}</div>
          <h1 className="title" style={{ fontSize: 15, margin: 0 }}>
            {scenario.name}
          </h1>
        </div>

        {/* Cascade: two consoles, one river. Switch with C. */}
        {secondarySnapshot && primarySnapshot && (
          <div role="group" aria-label="Plant console" style={{ display: 'flex', gap: 3 }}>
            {(
              [
                { slot: 'primary' as const, snap: primarySnapshot, label: 'Upstream' },
                { slot: 'secondary' as const, snap: secondarySnapshot, label: 'Downstream' },
              ]
            ).map(({ slot, snap: s2, label }) => {
              const active = activeSite === slot;
              const alarms = s2.alarms.filter(
                (a) => a.priority === 'emergency' || a.priority === 'high',
              ).length;
              return (
                <button
                  key={slot}
                  onClick={() => setActiveSite(slot)}
                  className={active ? 'primary' : undefined}
                  data-testid={`site-${slot}`}
                  aria-pressed={active}
                  style={{ textAlign: 'left', padding: '3px 8px', lineHeight: 1.25 }}
                  title={`${label} plant (C to switch)`}
                >
                  <div style={{ fontSize: 10, opacity: 0.75 }}>{label}</div>
                  <div className="num" style={{ fontSize: 12 }}>
                    {s2.headwater.toFixed(2)} m
                    {alarms > 0 && <span style={{ color: 'var(--alarm)' }}> ▲{alarms}</span>}
                  </div>
                </button>
              );
            })}
          </div>
        )}

        <div className="num" style={{ minWidth: 108 }}>
          <div className="label">Clock</div>
          <div style={{ fontSize: 16, fontWeight: 600 }}>{snap?.clock ?? '—'}</div>
        </div>

        <div style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
          <button onClick={engine.togglePause} aria-label={paused ? 'Resume' : 'Pause'} data-testid="pause">
            {paused ? '▶' : '❚❚'}
          </button>
          {SPEEDS.map((sp, i) => (
            <button
              key={sp}
              onClick={() => engine.setSpeed(sp)}
              className={speed === sp ? 'primary' : undefined}
              title={`Speed ${sp}× (key ${i + 1})`}
              style={{ padding: '4px 7px', fontVariantNumeric: 'tabular-nums' }}
            >
              {sp}×
            </button>
          ))}
        </div>

        <div className="num" style={{ minWidth: 88 }}>
          <div className="label">Weather</div>
          <div className="readout-sm">
            {snap ? temperature(snap.tempC, settings).value : '—'}
            <span className="unit">{settings.imperial ? '°F' : '°C'}</span>
          </div>
        </div>

        <div className="num" style={{ minWidth: 96 }}>
          <div className="label">Price</div>
          <div
            className="readout-sm"
            style={{ color: snap && snap.price < 0 ? 'var(--warn)' : 'var(--ink)' }}
          >
            {snap ? fmtPrice(snap.price).value : '—'}
            <span className="unit">/MWh</span>
          </div>
        </div>

        <div className="num" style={{ minWidth: 96 }}>
          <div className="label">Water value</div>
          <div className="readout-sm">
            {snap ? fmtPrice(snap.waterValue).value : '—'}
            <span className="unit">/MWh</span>
          </div>
        </div>

        <div className="num" style={{ minWidth: 104 }}>
          <div className="label">Net to date</div>
          <div className="readout-sm" style={{ color: snap && snap.net < 0 ? 'var(--alarm)' : 'var(--ink)' }}>
            {snap ? money(snap.net) : '—'}
          </div>
        </div>

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          {unacked > 0 && (
            <button
              className="danger"
              onClick={() => setView('alarms')}
              style={{ fontWeight: 700 }}
              data-testid="alarm-banner"
            >
              ▲ {unacked} unacknowledged {worst === 'emergency' ? 'EMERGENCY' : ''}
            </button>
          )}
          <button onClick={() => updateSettings({ showGlossary: !settings.showGlossary })} title="Glossary (?)">
            Glossary
          </button>
          <button onClick={() => updateSettings({ imperial: !settings.imperial })} title="Toggle units">
            {settings.imperial ? 'ft / cfs' : 'm / m³/s'}
          </button>
          <button onClick={() => engine.save(`${scenario.name} ${snap ? snap.clock : ''}`)}>Save</button>
        </div>
      </header>

      {/* ── Body ─────────────────────────────────────────────────────────────── */}
      <div className="app-body">
        <nav aria-label="Views" className="app-nav">
          {VIEWS.map((v) => {
            const active = view === v.id;
            const alarmsHere = snap ? snap.alarms.filter((a) => a.view === v.id).length : 0;
            return (
              <button
                key={v.id}
                onClick={() => setView(v.id)}
                data-testid={`nav-${v.id}`}
                aria-current={active ? 'page' : undefined}
                style={{
                  textAlign: 'left',
                  background: active ? 'var(--surface-2)' : 'transparent',
                  border: 'none',
                  borderLeft: `2px solid ${active ? 'var(--water-shallow)' : 'transparent'}`,
                  borderRadius: 0,
                  padding: '6px 8px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <span>{v.label}</span>
                <span style={{ color: 'var(--ink-dim)', fontSize: 10 }}>
                  {alarmsHere > 0 ? <span style={{ color: 'var(--alarm)' }}>▲{alarmsHere}</span> : v.key}
                </span>
              </button>
            );
          })}
          <div className="nav-speed" style={{ marginTop: 'auto', padding: 8 }}>
            <div className="label">Speed</div>
            <div className="num" style={{ fontSize: 12 }}>
              {speed}× {paused ? '· paused' : ''}
            </div>
          </div>
        </nav>

        <main className="scroll app-main">
          {fault && (
            <div
              className="panel alarm-active"
              role="alert"
              style={{ margin: 'var(--s3)', borderColor: 'var(--alarm)' }}
            >
              <div className="label" style={{ color: 'var(--alarm)' }}>
                Engine fault — simulation paused
              </div>
              <div>{fault}</div>
            </div>
          )}
          {view === 'control-room' && <ControlRoom />}
          {view === 'reservoir' && <ReservoirForecast />}
          {view === 'powerhouse' && <PowerhouseView />}
          {view === 'spillway' && <SpillwayView />}
          {view === 'river' && <RiverView />}
          {view === 'market' && <MarketView />}
          {view === 'alarms' && <AlarmsComms />}
          {view === 'debrief' && <Debrief />}
        </main>
      </div>

      <ConfirmDialog />
      <GlossaryDrawer />
      {toast && (
        <div
          role="status"
          style={{
            position: 'fixed',
            bottom: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'var(--surface-2)',
            border: '1px solid var(--line)',
            borderRadius: 3,
            padding: '6px 14px',
            zIndex: 60,
          }}
        >
          {toast}
        </div>
      )}
    </div>
  );
}
